import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const AnnouncementsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnnouncements();
  }, [selectedCategory]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const params = {
        active: 'true',
        limit: 20,
        sort: '-publishedAt'
      };
      
      // Only add category filter if not 'all'
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      
      const response = await apiService.getAnnouncements(params);
      setAnnouncements(response.data.announcements || []);
      setError('');
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Announcements', icon: 'fas fa-th' },
    { id: 'admission', name: 'Admission', icon: 'fas fa-bullhorn' },
    { id: 'examination', name: 'Examination', icon: 'fas fa-file-alt' },
    { id: 'event', name: 'Events', icon: 'fas fa-calendar' },
    { id: 'scholarship', name: 'Scholarship', icon: 'fas fa-award' },
    { id: 'placement', name: 'Placement', icon: 'fas fa-building' },
    { id: 'fee', name: 'Fee Notice', icon: 'fas fa-exclamation-triangle' },
    { id: 'result', name: 'Results', icon: 'fas fa-graduation-cap' },
    { id: 'workshop', name: 'Workshops', icon: 'fas fa-robot' },
    { id: 'holiday', name: 'Holidays', icon: 'fas fa-umbrella-beach' },
    { id: 'sports', name: 'Sports', icon: 'fas fa-trophy' }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      admission: 'Admission',
      examination: 'Examination',
      event: 'Event',
      scholarship: 'Scholarship',
      placement: 'Placement',
      fee: 'Fee Notice',
      result: 'Result',
      workshop: 'Workshop',
      holiday: 'Holiday',
      sports: 'Sports',
      general: 'General'
    };
    return categoryMap[category] || category;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-slate text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-sans font-bold mb-4">Announcements</h1>
          <p className="text-base sm:text-lg md:text-xl opacity-90 max-w-3xl">
            Stay updated with the latest news, events, and important notices from FiT Engineering College.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 sm:py-8 bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  selectedCategory === category.id
                    ? 'bg-crimson text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements List */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <i className="fas fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg text-slate">
                <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
                Loading announcements...
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:gap-6">
              {announcements.map(announcement => (
                <div 
                  key={announcement.id}
                  className={`bg-white rounded-lg p-6 border-l-4 shadow-sm hover:shadow-md transition-shadow ${
                    announcement.isPinned ? 'ring-2 ring-crimson ring-opacity-20' : ''
                  }`}
                  style={{ borderLeftColor: announcement.color === 'crimson' ? '#dc2626' : `var(--${announcement.color}, #6b7280)` }}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: announcement.color === 'crimson' ? '#fef2f2' : `rgba(107, 114, 128, 0.1)`,
                        color: announcement.color === 'crimson' ? '#dc2626' : '#6b7280'
                      }}
                    >
                      <i className={`${announcement.icon} text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <span 
                            className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white"
                            style={{ backgroundColor: announcement.color === 'crimson' ? '#dc2626' : '#6b7280' }}
                          >
                            {getCategoryDisplayName(announcement.category)}
                          </span>
                          {announcement.isPinned && (
                            <i className="fas fa-thumbtack text-crimson text-xs" title="Pinned"></i>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(announcement.publishedAt)}</span>
                      </div>
                      <h3 className="text-xl font-bold text-slate mb-2">{announcement.title}</h3>
                      <p className="text-gray-600">{announcement.description}</p>
                      {announcement.content && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button className="text-crimson font-semibold text-sm hover:underline">
                            Read More <i className="fas fa-arrow-right ml-1"></i>
                          </button>
                          <button className="text-gray-600 font-medium text-sm hover:text-crimson">
                            <i className="fas fa-share-alt mr-2"></i>Share
                          </button>
                        </div>
                      )}
                      {announcement.attachments && announcement.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                            <i className="fas fa-paperclip mr-1"></i>Attachments
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {announcement.attachments.map((attachment, idx) => {
                              const url = attachment.url
                                ? (attachment.url.startsWith('http') ? attachment.url : `${apiService.baseURL.replace(/\/api$/, '')}${attachment.url}`)
                                : '#';
                              const name = attachment.originalName || attachment.filename || `Attachment ${idx + 1}`;
                              const isPdf = (attachment.mimeType || '').includes('pdf') || name.toLowerCase().endsWith('.pdf');
                              const isDoc = (attachment.mimeType || '').includes('word') || name.toLowerCase().endsWith('.doc') || name.toLowerCase().endsWith('.docx');
                              const isImage = (attachment.mimeType || '').includes('image');
                              const icon = isPdf ? 'fas fa-file-pdf text-red-500' : isDoc ? 'fas fa-file-word text-blue-500' : isImage ? 'fas fa-file-image text-green-500' : 'fas fa-file text-gray-500';

                              return (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-slate hover:bg-crimson hover:text-white hover:border-crimson transition-colors"
                                >
                                  <i className={icon}></i>
                                  <span className="max-w-[150px] truncate">{name}</span>
                                  <i className="fas fa-external-link-alt text-xs opacity-60"></i>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {!loading && announcements.length === 0 && (
                <div className="text-center py-12">
                  <i className="fas fa-inbox text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No announcements found</h3>
                  <p className="text-gray-500">
                    {selectedCategory === 'all' 
                      ? 'There are no announcements available at the moment.' 
                      : `There are no announcements in the ${categories.find(c => c.id === selectedCategory)?.name || selectedCategory} category at the moment.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-12 sm:py-16 bg-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-4">Stay Updated</h2>
          <p className="text-gray-600 mb-8">
            Subscribe to our newsletter to receive the latest announcements directly in your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-crimson"
            />
            <button
              type="submit"
              className="bg-crimson hover:bg-red-800 text-white font-bold py-3 px-8 rounded-lg transition"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default AnnouncementsPage;